/**
 * Rich pattern context for emotional validation and user education
 * Each pattern includes psychological context to help users feel validated
 */

export interface PatternContext {
  definition: string
  whyItMatters: string
  emotionalImpact: string
  indicators: string[]
  prevalence: string
  validationMessage: string
}

export const PATTERN_CONTEXT: Record<string, PatternContext> = {
  Gaslighting: {
    definition:
      'A form of psychological manipulation where someone makes you question your own reality, memory, or perceptions.',
    whyItMatters:
      "Gaslighting erodes your sense of self and makes you dependent on the manipulator for your understanding of reality. It's one of the most damaging forms of emotional abuse.",
    emotionalImpact:
      'You may feel confused, anxious, or like you\'re "going crazy." You might constantly second-guess yourself or apologize even when you\'ve done nothing wrong.',
    indicators: [
      'Denying things they said or did, even with evidence',
      'Telling you that you\'re "too sensitive" or "overreacting"',
      'Twisting your words or rewriting history',
      "Making you feel like your emotions aren't valid",
    ],
    prevalence: 'Affects 1 in 3 relationships',
    validationMessage:
      "If you're seeing this pattern, trust yourself. Your feelings and memories are valid.",
  },
  Manipulation: {
    definition:
      'Tactics used to influence or control your behavior, decisions, or emotions through indirect, deceptive, or underhanded methods.',
    whyItMatters:
      'Manipulation undermines your autonomy and ability to make free choices. It creates an unequal power dynamic where your needs become secondary.',
    emotionalImpact:
      "You may feel trapped, obligated, or guilty for wanting to set boundaries. You might notice you're always compromising while they rarely do.",
    indicators: [
      'Using guilt or obligation to get their way',
      'Playing the victim to avoid accountability',
      'Giving conditional love or affection',
      'Creating situations where you "owe" them',
    ],
    prevalence: 'Present in 40% of unhealthy relationships',
    validationMessage:
      'Recognizing manipulation is the first step to reclaiming your power.',
  },
  Dismissiveness: {
    definition:
      'A pattern of minimizing, ignoring, or invalidating your thoughts, feelings, concerns, or experiences.',
    whyItMatters:
      "Being consistently dismissed teaches you that your inner world doesn't matter, leading to suppressed emotions and loss of self-worth.",
    emotionalImpact:
      'You may stop sharing your feelings, feel invisible, or believe your concerns are unimportant. You might feel lonely even when together.',
    indicators: [
      'Changing the subject when you share feelings',
      'Rolling eyes or sighing when you speak',
      'Saying "it\'s not a big deal" about things that matter to you',
      'Showing disinterest in your day or experiences',
    ],
    prevalence: 'Found in 45% of struggling relationships',
    validationMessage: 'Your feelings deserve to be heard. You matter.',
  },
  'Blame Shifting': {
    definition:
      'A deflection tactic where someone avoids responsibility by redirecting blame onto you or others.',
    whyItMatters:
      'Blame shifting prevents resolution and growth. It keeps you in a cycle of defending yourself instead of addressing real issues.',
    emotionalImpact:
      'You may feel like everything is your fault, even their bad behavior. You might constantly apologize or walk on eggshells.',
    indicators: [
      '"You made me do this" or "If you hadn\'t..."',
      'Bringing up your past mistakes during arguments',
      'Never apologizing or admitting fault',
      'Turning every conversation about their behavior into your flaws',
    ],
    prevalence: 'Occurs in 50% of toxic dynamics',
    validationMessage:
      "You are not responsible for someone else's choices or reactions.",
  },
  'Emotional Abuse': {
    definition:
      'A pattern of behavior that harms your emotional well-being through intimidation, criticism, humiliation, or control.',
    whyItMatters:
      'Emotional abuse can be as damaging as physical abuse, affecting your mental health, self-esteem, and ability to trust.',
    emotionalImpact:
      'You may feel worthless, anxious, or depressed. You might have lost touch with who you were before this relationship.',
    indicators: [
      'Constant criticism or put-downs',
      'Name-calling or cruel "jokes" at your expense',
      'Threats or intimidation',
      'Isolating you from friends and family',
    ],
    prevalence: 'Affects 35% of all relationships',
    validationMessage:
      "What you're experiencing is real. You deserve to be treated with respect.",
  },
  'Passive Aggression': {
    definition:
      'Indirect expression of hostility through subtle behaviors like sarcasm, silent treatment, or deliberate inefficiency.',
    whyItMatters:
      "Passive aggression creates confusion and prevents honest communication. It's a way of expressing anger while avoiding direct confrontation.",
    emotionalImpact:
      "You may feel like you're always reading between the lines or walking on eggshells. You might doubt whether you're overreacting.",
    indicators: [
      'Sarcastic comments disguised as jokes',
      'Agreeing to things then "forgetting" or doing them poorly',
      'Giving the silent treatment instead of discussing issues',
      'Backhanded compliments',
    ],
    prevalence: 'Present in 55% of communication issues',
    validationMessage:
      "You're not imagining the tension. Indirect hostility is still hostility.",
  },
  'Love Bombing': {
    definition:
      'Overwhelming someone with excessive affection, attention, and praise early in a relationship to gain control.',
    whyItMatters:
      'Love bombing creates an artificial sense of intimacy and makes you feel obligated. It often precedes more controlling behavior.',
    emotionalImpact:
      'You may feel swept off your feet initially, then confused when the intensity fades or turns to criticism.',
    indicators: [
      'Excessive gifts or grand gestures very early on',
      'Constant texting and need for contact',
      'Declaring love or commitment unusually fast',
      'Making you feel like you\'re their "everything"',
    ],
    prevalence: 'Common in 30% of manipulative relationships',
    validationMessage:
      "Healthy love grows gradually. Intensity isn't the same as depth.",
  },
  'Silent Treatment': {
    definition:
      'Deliberately ignoring or refusing to communicate as a form of punishment or control.',
    whyItMatters:
      'The silent treatment is emotional manipulation that creates anxiety and forces you to chase resolution on their terms.',
    emotionalImpact:
      'You may feel abandoned, anxious, or desperate to "fix" things. You might find yourself apologizing just to end the silence.',
    indicators: [
      'Refusing to speak for extended periods',
      'Ignoring your messages or presence',
      "Pretending you don't exist",
      'Only breaking silence when they want something',
    ],
    prevalence: 'Used in 60% of controlling relationships',
    validationMessage:
      'You deserve communication, not punishment through silence.',
  },
  'Guilt Tripping': {
    definition:
      'Making someone feel guilty to manipulate their behavior or decisions.',
    whyItMatters:
      'Guilt tripping exploits your empathy and care to control you. It makes setting boundaries feel selfish.',
    emotionalImpact:
      'You may feel responsible for their happiness, unable to say no, or guilty for having your own needs.',
    indicators: [
      '"After everything I\'ve done for you..."',
      'Sighing or acting hurt when you set boundaries',
      "Bringing up sacrifices they've made",
      'Making you feel bad for spending time with others',
    ],
    prevalence: 'Found in 65% of unhealthy dynamics',
    validationMessage:
      "Having boundaries doesn't make you selfish. It makes you healthy.",
  },
  'Controlling Behavior': {
    definition:
      'Attempts to dictate or restrict your choices, relationships, activities, or autonomy.',
    whyItMatters:
      'Control erodes your independence and self-trust. It often escalates over time and can become dangerous.',
    emotionalImpact:
      'You may feel trapped, monitored, or like you need permission to live your life. You might have given up hobbies or friendships.',
    indicators: [
      'Monitoring your phone, social media, or location',
      'Dictating what you wear or who you see',
      'Making financial decisions without you',
      'Getting angry when you make independent choices',
    ],
    prevalence: 'Present in 25% of relationships',
    validationMessage:
      'You have the right to your own life, choices, and privacy.',
  },
  Triangulation: {
    definition:
      'Bringing a third person into the dynamic to create jealousy, insecurity, or to validate their position.',
    whyItMatters:
      'Triangulation destabilizes your sense of security and keeps you competing for their attention or approval.',
    emotionalImpact:
      'You may feel jealous, insecure, or like you\'re constantly being compared. You might feel you need to "earn" their loyalty.',
    indicators: [
      'Frequently mentioning exes or admirers',
      'Comparing you unfavorably to others',
      'Using others to relay messages or take sides',
      'Flirting with others to make you jealous',
    ],
    prevalence: 'Occurs in 35% of toxic relationships',
    validationMessage:
      "You shouldn't have to compete for basic respect and loyalty.",
  },
  Invalidation: {
    definition:
      'Rejecting, dismissing, or minimizing your feelings, thoughts, or experiences as wrong or unimportant.',
    whyItMatters:
      'Chronic invalidation teaches you to distrust your own emotions and perceptions, damaging your self-concept.',
    emotionalImpact:
      'You may feel like your emotions are "wrong" or that you\'re too sensitive. You might stop trusting your own judgment.',
    indicators: [
      '"You shouldn\'t feel that way"',
      'Telling you your reaction is wrong',
      'Dismissing your experiences as exaggerated',
      'Making you feel crazy for having emotions',
    ],
    prevalence: 'Present in 50% of unhealthy communication',
    validationMessage: 'Your feelings are valid simply because you feel them.',
  },
  Stonewalling: {
    definition:
      'Completely withdrawing from interaction, refusing to engage or respond during conflict.',
    whyItMatters:
      "Stonewalling prevents resolution and leaves issues festering. It's a form of emotional abandonment.",
    emotionalImpact:
      "You may feel desperate, frustrated, or like you're talking to a wall. You might escalate just to get any response.",
    indicators: [
      'Walking away during important conversations',
      'Refusing to discuss problems',
      'Giving one-word answers or grunts',
      'Physically present but emotionally absent',
    ],
    prevalence: 'Found in 40% of failing relationships',
    validationMessage:
      'You deserve a partner who shows up for difficult conversations.',
  },
  'Verbal Abuse': {
    definition:
      'Using words to attack, demean, threaten, or control another person.',
    whyItMatters:
      'Verbal abuse causes lasting psychological harm and often escalates. Words can wound as deeply as actions.',
    emotionalImpact:
      'You may feel worthless, afraid, or like you deserve the treatment. You might have internalized their cruel words.',
    indicators: [
      'Yelling, screaming, or aggressive tone',
      'Insults, name-calling, or degrading language',
      'Threats or intimidation',
      'Public humiliation',
    ],
    prevalence: 'Affects 30% of relationships',
    validationMessage:
      'No one deserves to be spoken to with cruelty. This is not love.',
  },
  'Coercive Control': {
    definition:
      'A pattern of controlling behaviors that create an unequal power dynamic and limit your freedom.',
    whyItMatters:
      'Coercive control is recognized as a form of domestic abuse. It systematically strips away your autonomy and independence.',
    emotionalImpact:
      "You may feel like you've lost yourself, are constantly monitored, or need permission for basic activities.",
    indicators: [
      'Controlling finances or access to money',
      'Isolating you from support systems',
      'Monitoring your movements and communications',
      'Making rules you must follow',
    ],
    prevalence: 'Present in 20% of abusive relationships',
    validationMessage: 'This is abuse. You deserve freedom and autonomy.',
  },
  Minimization: {
    definition:
      'Downplaying the severity or impact of harmful behavior, making it seem less serious than it is.',
    whyItMatters:
      'Minimization prevents accountability and makes you question whether your concerns are valid.',
    emotionalImpact:
      'You may feel like you\'re making a big deal out of nothing, or that you should just "get over it."',
    indicators: [
      '"It wasn\'t that bad" or "You\'re overreacting"',
      'Comparing to worse situations to dismiss yours',
      'Laughing off serious concerns',
      'Refusing to acknowledge impact of their actions',
    ],
    prevalence: 'Found in 55% of toxic dynamics',
    validationMessage:
      'Your pain is real, regardless of how they try to minimize it.',
  },
}

/**
 * Get pattern context with fuzzy matching for pattern type strings
 */
export function getPatternContext(patternType: string): PatternContext | null {
  if (!patternType) return null

  // Direct match
  if (PATTERN_CONTEXT[patternType]) {
    return PATTERN_CONTEXT[patternType]
  }

  // Case-insensitive match
  const normalizedInput = patternType.toLowerCase().trim()
  for (const [key, value] of Object.entries(PATTERN_CONTEXT)) {
    if (key.toLowerCase() === normalizedInput) {
      return value
    }
  }

  // Fuzzy match - check if input contains or is contained by a key
  for (const [key, value] of Object.entries(PATTERN_CONTEXT)) {
    const normalizedKey = key.toLowerCase()
    if (
      normalizedInput.includes(normalizedKey) ||
      normalizedKey.includes(normalizedInput)
    ) {
      return value
    }
  }

  // Partial word match
  const inputWords = normalizedInput.split(/[\s_-]+/)
  for (const [key, value] of Object.entries(PATTERN_CONTEXT)) {
    const keyWords = key.toLowerCase().split(/[\s_-]+/)
    if (
      inputWords.some((word) =>
        keyWords.some((kw) => kw.includes(word) || word.includes(kw)),
      )
    ) {
      return value
    }
  }

  return null
}

/**
 * Get a validation message based on risk level and pattern count
 */
export function getValidationMessage(
  riskLevel: 'low' | 'medium' | 'high',
  patternCount: number,
): string {
  if (riskLevel === 'high') {
    return `We found ${patternCount} concerning patterns in this conversation. Trust your instincts — what you're feeling is valid.`
  }
  if (riskLevel === 'medium') {
    return `We detected ${patternCount} patterns worth paying attention to. Your awareness of these dynamics is an important first step.`
  }
  return patternCount > 0
    ? `We found ${patternCount} minor patterns. Overall, this communication appears relatively healthy.`
    : 'This conversation shows healthy communication patterns. Keep nurturing this dynamic.'
}

/**
 * Get emotional support copy based on detected patterns
 */
export function getEmotionalSupportCopy(patterns: string[]): string {
  if (patterns.length === 0) return ''

  const hasGaslighting = patterns.some((p) =>
    p.toLowerCase().includes('gaslight'),
  )
  const hasAbuse = patterns.some((p) => p.toLowerCase().includes('abuse'))
  const hasControl = patterns.some(
    (p) =>
      p.toLowerCase().includes('control') ||
      p.toLowerCase().includes('coercive'),
  )

  if (hasAbuse || hasControl) {
    return "What you're experiencing is serious. Consider reaching out to a trusted friend, family member, or professional for support."
  }

  if (hasGaslighting) {
    return "If you've been questioning your own reality, know that your perceptions are valid. This analysis confirms what you may have been feeling."
  }

  return "Recognizing these patterns is a sign of strength. You're taking an important step toward healthier relationships."
}

/**
 * Get all pattern types as an array
 */
export function getAllPatternTypes(): string[] {
  return Object.keys(PATTERN_CONTEXT)
}
